import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, MessageCircle, Clock } from "lucide-react";
import { fadeUp, waLink, telLink } from "@/lib/site-utils";
import { CONTACT, MAPS_EMBED } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactLocation = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("नाम और फ़ोन ज़रूरी / Name & phone required");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("संदेश मिल गया! हम जल्द संपर्क करेंगे / We'll reach out soon");
      setForm({ name: "", phone: "", message: "" });
    } catch (e) {
      toast.error("कुछ गड़बड़ी हुई / Something went wrong");
    } finally {
      setSending(false);
    }
  };

  const cls = "w-full bg-transparent border-b border-white/20 focus:border-jcb outline-none py-3 font-hind text-[#f5f5f0] placeholder:text-neutral-500 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14">
          <span className="font-plex text-xs tracking-[0.25em] uppercase text-jcb">संपर्क / CONTACT US</span>
          <h2 className="mt-4 font-khand font-700 uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl text-[#f5f5f0]">
            आइए बात करें
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: info + form */}
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <a data-testid="contact-call" href={telLink()} className="group border border-white/10 bg-stone/40 p-6 hover:bg-jcb transition-colors duration-200">
                <Phone className="text-jcb group-hover:text-black" />
                <p className="mt-4 font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400 group-hover:text-black/70">Call</p>
                <p className="font-khand text-2xl text-[#f5f5f0] group-hover:text-black">{CONTACT.phoneDisplay}</p>
              </a>
              <a data-testid="contact-whatsapp" href={waLink("नमस्ते SS Group!")} target="_blank" rel="noreferrer" className="group border border-white/10 bg-stone/40 p-6 hover:bg-[#25D366] transition-colors duration-200">
                <MessageCircle className="text-[#25D366] group-hover:text-black" />
                <p className="mt-4 font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400 group-hover:text-black/70">WhatsApp</p>
                <p className="font-khand text-2xl text-[#f5f5f0] group-hover:text-black">{CONTACT.phoneDisplay}</p>
              </a>
            </div>

            <div className="border border-white/10 bg-stone/40 p-6 flex items-start gap-4">
              <MapPin className="text-terra shrink-0 mt-1" />
              <div>
                <p className="font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400">पता / Address</p>
                <p className="font-hind text-[#f5f5f0] leading-relaxed mt-1">{CONTACT.address}</p>
              </div>
            </div>

            <div className="border border-white/10 bg-stone/40 p-6 flex items-center gap-4">
              <Clock className="text-jcb shrink-0" />
              <p className="font-hind text-[#f5f5f0]">रोज़ खुला / Open Daily — 24×7 बुकिंग उपलब्ध</p>
            </div>

            <form data-testid="contact-form" onSubmit={submit} className="border border-white/10 bg-stone/40 p-6 md:p-8 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="नाम / Name" className={cls} />
                <input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="फ़ोन / Phone" className={cls} inputMode="tel" />
              </div>
              <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="संदेश / Message" className={`${cls} resize-none`} />
              <button data-testid="contact-submit-btn" type="submit" disabled={sending} className="w-full bg-jcb text-black py-4 font-khand font-600 text-lg tracking-wide hover:bg-white transition-colors duration-200 disabled:opacity-60">
                {sending ? "भेज रहे हैं…" : "संदेश भेजें / Send Message"}
              </button>
            </form>
          </div>

          {/* Right: map */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="border border-white/10 min-h-[500px] lg:min-h-full overflow-hidden">
            <iframe
              title="SS Group Location"
              data-testid="google-map"
              src={MAPS_EMBED}
              className="w-full h-full min-h-[500px] grayscale-[0.3] contrast-[1.1]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
